package world.phantasmal.web.questEditor.rendering

import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.launch
import world.phantasmal.cell.list.ListCell
import world.phantasmal.cell.list.ListChangeEvent
import world.phantasmal.cell.mutateDeferred
import world.phantasmal.core.disposable.Disposer
import world.phantasmal.core.disposable.DisposableSupervisedScope
import world.phantasmal.psolib.Episode
import world.phantasmal.psolib.asm.dataFlowAnalysis.ParticleSpawn
import world.phantasmal.web.questEditor.loading.AreaAssetLoader
import world.phantasmal.web.questEditor.loading.EntityAssetLoader
import world.phantasmal.web.questEditor.loading.ParticleAssetLoader
import world.phantasmal.web.questEditor.models.AreaVariantModel
import world.phantasmal.web.questEditor.models.QuestNpcModel
import world.phantasmal.web.questEditor.models.QuestObjectModel
import world.phantasmal.web.questEditor.stores.AreaStore
import world.phantasmal.web.questEditor.stores.PlaybackVisualizationStore
import world.phantasmal.web.questEditor.stores.QuestEditorRenderAccess
import world.phantasmal.web.questEditor.stores.QuestEntitySelectionActions
import world.phantasmal.web.questEditor.stores.QuestEditorUiStore
import world.phantasmal.webui.DisposableContainer
import world.phantasmal.web.core.rendering.disposeObject3DResources
import world.phantasmal.web.externals.three.Group
import world.phantasmal.web.externals.three.Vector3

/**
 * Loads the necessary area and entity 3D models into [QuestRenderer].
 */
abstract class QuestMeshManager protected constructor(
    areaAssetLoader: AreaAssetLoader,
    entityAssetLoader: EntityAssetLoader,
    particleAssetLoader: ParticleAssetLoader,
    private val questEditorStore: QuestEditorRenderAccess,
    questEditorUiStore: QuestEditorUiStore,
    playbackVisualizationStore: PlaybackVisualizationStore,
    areaStore: AreaStore,
    private val renderContext: QuestRenderContext,
) : DisposableContainer() {
    private val scope = addDisposable(DisposableSupervisedScope(this::class, Dispatchers.Default))
    private val npcObserver = addDisposable(Disposer())
    private val objectObserver = addDisposable(Disposer())
    private val areaMeshManager = AreaMeshManager(renderContext, areaAssetLoader)
    private val npcGroundingManager = addDisposable(
        NpcGroundingManager(questEditorStore.npcPlacementPolicy, renderContext)
    )
    private val challengePreviewMeshManager = addDisposable(
        ChallengePreviewMeshManager(
            questEditorStore,
            questEditorUiStore,
            renderContext,
            entityAssetLoader,
        )
    )
    private val labelManager = addDisposable(
        LabelManager(
            questEditorStore,
            questEditorUiStore,
            playbackVisualizationStore,
            renderContext,
            areaStore,
        )
    )
    private val selectionVisualizationManager = addDisposable(
        SelectionVisualizationManager(
            questEditorStore,
            renderContext,
            labelManager.sectionIdRenderer,
        )
    )
    private val npcMeshManager = addDisposable(
        EntityMeshManager(
            questEditorStore,
            questEditorUiStore,
            renderContext,
            entityAssetLoader,
        )
    )
    private val objectMeshManager = addDisposable(
        EntityMeshManager(
            questEditorStore,
            questEditorUiStore,
            renderContext,
            entityAssetLoader,
        )
    )
    private val particleMarkerManager = addDisposable(ParticleMarkerManager(renderContext, particleAssetLoader))

    // Origin point rendering
    private val originPointRenderer = OriginPointRenderer()
    private var originGroup: Group? = null

    private var areaLoadJob: Job? = null
    private var npcLoadJob: Job? = null
    private var objectLoadJob: Job? = null
    private var particleLoadJob: Job? = null

    init {
        // Observe origin point show/hide state
        observeNow(questEditorUiStore.showOriginPoint) { showOrigin ->
            updateOriginPointVisibility(showOrigin)
        }
    }

    override fun dispose() {
        originGroup?.let { group ->
            renderContext.scene.remove(group)
            disposeObject3DResources(group)
            originGroup = null
        }
        super.dispose()
    }

    protected fun loadAreaMeshes(
        episode: Episode?,
        areaVariant: AreaVariantModel?,
        ultimate: Boolean,
    ) {
        areaLoadJob?.cancel()
        areaLoadJob = scope.launch {
            areaMeshManager.load(episode, areaVariant, ultimate)
        }
    }

    protected fun loadNpcMeshes(npcs: ListCell<QuestNpcModel>) {
        npcLoadJob?.cancel()
        npcLoadJob = scope.launch {
            npcObserver.disposeAll()
            npcMeshManager.removeAll()

            npcs.value.forEach(npcMeshManager::add)

            npcObserver.add(npcs.observeListChange(::npcsChanged))
        }
    }

    protected fun loadObjectMeshes(objects: ListCell<QuestObjectModel>) {
        objectLoadJob?.cancel()
        objectLoadJob = scope.launch {
            objectObserver.disposeAll()
            objectMeshManager.removeAll()

            objects.value.forEach(objectMeshManager::add)
            selectionVisualizationManager.setVisibleObjects(objects.value)

            objectObserver.add(objects.observeListChange { event ->
                objectsChanged(event)
                selectionVisualizationManager.setVisibleObjects(objects.value)
            })
        }
    }

    protected fun loadParticleMarkers(
        spawns: List<ParticleSpawn>,
        resolveTemplateMapIds: (ParticleSpawn) -> Set<Int> = { emptySet() },
        resolveEntityPosition: (Int) -> Vector3? = { null },
    ) {
        particleLoadJob?.cancel()
        particleLoadJob = scope.launch {
            particleMarkerManager.setSpawns(spawns, resolveTemplateMapIds, resolveEntityPosition)
        }
    }

    private fun npcsChanged(event: ListChangeEvent<QuestNpcModel>) {
        reconcileDetachedScriptNpcSelection(questEditorStore, event)

        for (change in event.changes) {
            change.removed.forEach(npcMeshManager::remove)
            change.inserted.forEach(npcMeshManager::add)
        }
    }

    private fun objectsChanged(event: ListChangeEvent<QuestObjectModel>) {
        for (change in event.changes) {
            change.removed.forEach(objectMeshManager::remove)
            change.inserted.forEach(objectMeshManager::add)
        }
    }

    /**
     * Updates the visibility of the origin point based on the store state.
     */
    private fun updateOriginPointVisibility(showOrigin: Boolean) {
        if (showOrigin) {
            if (originGroup == null) {
                originGroup = originPointRenderer.createOriginPointVisualization()
                renderContext.scene.add(originGroup!!)
            }
        } else {
            originGroup?.let { group ->
                renderContext.scene.remove(group)
                disposeObject3DResources(group)
                originGroup = null
            }
        }
    }

    /**
     * Called before each render to update text scales for constant screen size.
     */
    open fun beforeRender() {
        labelManager.beforeRender()
        particleMarkerManager.beforeRender()

        // Update origin point axis labels (billboard + constant screen size)
        originGroup?.let { group ->
            originPointRenderer.updateLabels(renderContext.camera, group)
        }
    }
}

/**
 * Script NPC preview models are derived values and are recreated when their analysis changes.
 * Keep selection/highlight attached to an equivalent replacement, or clear it when that preview
 * no longer exists. Otherwise the editor keeps exposing a model that has been removed from the
 * scene.
 */
internal fun reconcileDetachedScriptNpcSelection(
    store: QuestEntitySelectionActions,
    event: ListChangeEvent<QuestNpcModel>,
) {
    val removed = event.changes.flatMap { it.removed }
    val inserted = event.changes.flatMap { it.inserted }

    fun replacementFor(entity: QuestNpcModel?): QuestNpcModel? {
        val spawn = entity?.scriptSpawn ?: return entity
        if (removed.none { it === entity }) return entity

        return inserted.firstOrNull { candidate ->
            candidate.floorId == entity.floorId && candidate.scriptSpawn == spawn
        }
    }

    val selected = store.selectedEntity.value as? QuestNpcModel
    val highlighted = store.highlightedEntity.value as? QuestNpcModel
    val selectedReplacement = replacementFor(selected)
    val highlightedReplacement = replacementFor(highlighted)

    if (selectedReplacement === selected && highlightedReplacement === highlighted) return

    mutateDeferred {
        if (store.selectedEntity.value === selected) {
            store.setSelectedEntity(selectedReplacement)
        }
        if (store.highlightedEntity.value === highlighted) {
            store.setHighlightedEntity(highlightedReplacement)
        }
    }
}
